import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// Icons
const ChevronDownIcon = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      width: 14,
      height: 14,
      transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      color: "#6b7280",
      flexShrink: 0,
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14, color: "#9ca3af", flexShrink: 0 }}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14, color: "#4f46e5", flexShrink: 0 }}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClearIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 13, height: 13, color: "#9ca3af" }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "— Select —",
  searchPlaceholder = "Search...",
  disabled = false,
  className = "",
  style = {},
  error = false,
  allowCustom = false,
  isClearable = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [coords, setCoords] = useState({ top: 0, bottom: "auto", left: 0, width: 0, maxHeight: 260 });
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize options array to [{ value, label }]
  const normalizedOptions = options.map((opt) => {
    if (typeof opt === "object" && opt !== null) {
      const val = opt.value ?? opt.id ?? opt.name;
      const lbl = opt.label ?? opt.name ?? String(val);
      return { value: val, label: String(lbl) };
    }
    return { value: opt, label: String(opt) };
  });

  const selectedOption = normalizedOptions.find(
    (opt) => String(opt.value) === String(value)
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isExactMatch = normalizedOptions.some(
    (opt) => String(opt.label).toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Prefer downwards; only open upwards if very tight below (<140px) and there's more room above
      const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
      const width = Math.max(rect.width, 180);
      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = Math.max(10, window.innerWidth - width - 10);
      }
      if (left < 10) left = 10;

      if (openUp) {
        setCoords({
          top: "auto",
          bottom: window.innerHeight - rect.top + 4,
          left,
          width,
          maxHeight: Math.min(260, Math.max(120, spaceAbove - 16)),
        });
      } else {
        setCoords({
          top: rect.bottom + 4,
          bottom: "auto",
          left,
          width,
          maxHeight: Math.min(260, Math.max(120, spaceBelow - 16)),
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      if (searchInputRef.current) {
        searchInputRef.current.focus({ preventScroll: true });
      }
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const displayLabel = selectedOption ? selectedOption.label : (value || placeholder);
  const hasValue = Boolean(selectedOption || value);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.("");
    setSearchTerm("");
  };

  return (
    <div
      ref={containerRef}
      className={`cjn-dropdown-wrap ${className}`}
      style={{ position: "relative", width: "100%", ...style }}
    >
      <div
        className={`cjn-input ${error ? "err" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          cursor: disabled ? "not-allowed" : "pointer",
          background: disabled ? "#f9fafb" : "#ffffff",
          color: hasValue && selectedOption ? "#0f172a" : "#94a3b8",
          borderColor: error ? "#f87171" : isOpen ? "#6366f1" : "#e2e8f0",
          boxShadow: isOpen
            ? "0 0 0 3px rgba(99, 102, 241, 0.12)"
            : "0 1px 2px rgba(0,0,0,0.02)",
          borderRadius: "8px",
          padding: "8px 12px",
          minHeight: "38px",
          transition: "all 0.15s ease",
          userSelect: "none",
        }}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) updatePosition();
            setIsOpen((prev) => !prev);
          }
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "0.86rem",
            fontWeight: hasValue && selectedOption ? 500 : 400,
            flex: 1,
          }}
        >
          {displayLabel}
        </span>
        
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          {isClearable && hasValue && !disabled && (
            <span
              onClick={handleClear}
              title="Clear selection"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#f1f5f9",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#e2e8f0")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f1f5f9")}
            >
              <ClearIcon />
            </span>
          )}
          <ChevronDownIcon open={isOpen} />
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="cjn-dropdown-menu"
            style={{
              position: "fixed",
              top: coords.top !== "auto" ? `${coords.top}px` : "auto",
              bottom: coords.bottom !== "auto" ? `${coords.bottom}px` : "auto",
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              maxHeight: `${coords.maxHeight}px`,
              zIndex: 999999,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              boxShadow:
                "0 12px 28px -4px rgba(15, 23, 42, 0.15), 0 4px 12px -2px rgba(15, 23, 42, 0.08)",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              animation: "fadeIn 0.15s ease",
              boxSizing: "border-box",
            }}
          >
            {/* Search box header */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                marginBottom: "6px",
                flexShrink: 0,
              }}
            >
              <span style={{ position: "absolute", left: "10px", pointerEvents: "none" }}>
                <SearchIcon />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="cjn-input"
                style={{
                  padding: "6px 10px 6px 30px",
                  fontSize: "0.82rem",
                  width: "100%",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  outline: "none",
                  background: "#f8fafc",
                }}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div style={{ flex: 1, overflowY: "auto", minHeight: 0, paddingRight: "2px", paddingBottom: "8px" }}>
              {allowCustom && searchTerm.trim() !== "" && !isExactMatch && (
                <div
                  className="cjn-dropdown-item"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.83rem",
                    background: "#eef2ff",
                    color: "#4338ca",
                    fontWeight: 600,
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onClick={() => {
                    onChange(searchTerm.trim());
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <span>+</span> Use "{searchTerm.trim()}"
                </div>
              )}

              {filteredOptions.length === 0 && (!allowCustom || searchTerm.trim() === "") ? (
                <div
                  style={{
                    padding: "16px 8px",
                    fontSize: "0.82rem",
                    color: "#94a3b8",
                    textAlign: "center",
                  }}
                >
                  No matching options
                </div>
              ) : (
                filteredOptions.map((opt, idx) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <div
                      key={opt.value ?? idx}
                      className="cjn-dropdown-item"
                      style={{
                        padding: "8px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.84rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: isSelected ? "#eef2ff" : "transparent",
                        color: isSelected ? "#4338ca" : "#334155",
                        fontWeight: isSelected ? 600 : 400,
                        marginBottom: "2px",
                        transition: "background 0.12s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "#f1f5f9";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "transparent";
                      }}
                      onClick={() => {
                        onChange(opt.value);
                        setIsOpen(false);
                        setSearchTerm("");
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {opt.label}
                      </span>
                      {isSelected && <CheckIcon />}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}


